import React, {useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import get from 'lodash/get'
import Card from 'src/components-new/layout/Card'
import Button from 'src/components-new/primitives/Button'
import TextAreaNew from 'src/components-new/inputs/TextAreaNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {formatDate, formatUnitOfMeasure} from './therapyFormatters'

// Ported from createTherapyContent in
// src/components/templates/PatientDetailsComponent/TherapyCard/TherapyCard.tsx (~L90-210).
// Renders a PSP-config-driven (dataSchema) read-only view of a single therapy, with a single
// exception: the "introductoryMeetingNotes" text-area becomes editable when editable=true.

export interface TherapyDataSchemaElement {
  name: string
  value: string
  type?: string
}

interface DynamicTherapyFormProps {
  therapy: Record<string, any>
  dataSchema: TherapyDataSchemaElement[]
  editable: boolean
  onSave: (patch: Record<string, any>) => Promise<void>
}

const THERAPY_DEVICE_OF_ASSUMPTIONS: Record<string, string> = {
  PEN: 'Penna',
  SYRINGE: 'Siringa preriempita',
  PUMP: 'Pompa',
  INHALER: 'Inalatore'
}

const ASSUMPTION_DOSAGE_SUB_FIELDS = [
  'assumptionDosage.infusionRate',
  'assumptionDosage.secondaryDosage',
  'assumptionDosage.dilution',
  'assumptionDosage'
]

// Fields that must never be shown in the new-style therapy view, regardless of what the
// PSP-configured dataSchema sends. "Velocità infusionale del device" (speedOfAssumption.value)
// was dropped from the new style per product decision.
// TODO: remove this filter once the field is removed from the PSP dataSchema config itself
// (backend/configuratore), making this frontend-side exclusion unnecessary.
const HIDDEN_FIELDS = ['speedOfAssumption.value']

// Delivery-device fields ("Dispositivo", "Velocità device") must render grouped together,
// after the custom-dosages block and before doctor notes — regardless of where they sit in the
// PSP-configured dataSchema order.
function isDeviceField(element: TherapyDataSchemaElement): boolean {
  return element.type === 'device' || element.value === 'speedOfDevice' || element.value.startsWith('speedOfDevice.')
}

function Field({label, value}: {label: string; value: React.ReactNode}): JSX.Element {
  return (
    <Box>
      <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.4}}>
        {label}
      </Typography>
      <Typography variant="body2">{value || value === 0 ? value : '—'}</Typography>
    </Box>
  )
}

function TwoColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
        gap: 2
      }}
    >
      {children}
    </Box>
  )
}

interface EditableNotesFieldProps {
  label: string
  initialValue: string
  onSave: (patch: Record<string, any>) => Promise<void>
  fieldName: string
}

function EditableNotesField({label, initialValue, onSave, fieldName}: EditableNotesFieldProps): JSX.Element {
  const [value, setValue] = useState<string>(initialValue ?? '')
  const [saving, setSaving] = useState<boolean>(false)

  useEffect(() => {
    setValue(initialValue ?? '')
  }, [initialValue])

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave({[fieldName]: value})
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1}}>
      <TextAreaNew label={label} value={value} onChange={setValue} rows={4} fullWidth />
      <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button variant="primary" loading={saving} onClick={handleSave}>Salva</Button>
      </Box>
    </Box>
  )
}

function CustomDosagesBlock({
  therapy,
  dataSchema
}: {
  therapy: Record<string, any>
  dataSchema: TherapyDataSchemaElement[]
}): JSX.Element {
  const propertyIsInDataSchema = (key: string) => dataSchema.some(element => element.value === key)
  const propertyInDataSchemaName = (key: string) => dataSchema.find(element => element.value === key)?.name ?? key
  const customDosages: Record<string, any>[] = get(therapy, 'customDosages') ?? []

  return (
    <Box sx={{mt: 3}}>
      <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 1, textTransform: 'uppercase', letterSpacing: 0.4}}>
        Dosaggi
      </Typography>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        {customDosages.map((dosage, index) => (
          <Card key={index} title={`Assunzione ${index + 1}`}>
            <TwoColGrid>
              <Field label="Dosaggio" value={formatUnitOfMeasure(dosage)} />
              {propertyIsInDataSchema('assumptionDosage.infusionRate') && (
                <Field label={propertyInDataSchemaName('assumptionDosage.infusionRate')} value={formatUnitOfMeasure(dosage.infusionRate)} />
              )}
              {propertyIsInDataSchema('assumptionDosage.secondaryDosage') && (
                <Field label={propertyInDataSchemaName('assumptionDosage.secondaryDosage')} value={formatUnitOfMeasure(dosage.secondaryDosage)} />
              )}
              {propertyIsInDataSchema('assumptionDosage.dilution') && (
                <Field label={propertyInDataSchemaName('assumptionDosage.dilution')} value={dosage.dilution} />
              )}
            </TwoColGrid>
          </Card>
        ))}
      </Box>
    </Box>
  )
}

export default function DynamicTherapyForm({therapy, dataSchema: rawDataSchema, editable, onSave}: DynamicTherapyFormProps): JSX.Element {
  const dataSchema = rawDataSchema.filter(element => !HIDDEN_FIELDS.includes(element.value))
  const therapyHasAssumptionDosage = Boolean(get(therapy, 'assumptionDosage'))
  const schemaHasAssumptionDosageType = dataSchema.some(element => element.type === 'assumptionDosage')
  const shouldRenderCustomDosages = schemaHasAssumptionDosageType && !therapyHasAssumptionDosage

  const visibleSchema = shouldRenderCustomDosages
    ? dataSchema.filter(element => !ASSUMPTION_DOSAGE_SUB_FIELDS.includes(element.value))
    : dataSchema

  // Doctor notes (text-area fields, e.g. "introductoryMeetingNotes") must always render last,
  // after the custom-dosages block — regardless of where they sit in the PSP-configured
  // dataSchema order. Delivery-device fields render in their own group right before notes,
  // after the custom-dosages block.
  const generalSchema = visibleSchema.filter(element => element.type !== 'text-area' && !isDeviceField(element))
  const deviceSchema = visibleSchema.filter(isDeviceField)
  const notesSchema = visibleSchema.filter(element => element.type === 'text-area')

  const renderField = (element: TherapyDataSchemaElement): React.ReactNode => {
    const rawValue = get(therapy, element.value)

    if (element.type === 'date') {
      return <Field key={element.value} label={element.name} value={formatDate(rawValue, 'DD MMM YYYY')} />
    }

    if (element.type === 'boolean') {
      return <Field key={element.value} label={element.name} value={rawValue ? 'Sì' : 'No'} />
    }

    if (element.type === 'text-area') {
      if (element.value === 'introductoryMeetingNotes' && editable) {
        return (
          <EditableNotesField
            key={element.value}
            label={element.name}
            initialValue={rawValue}
            onSave={onSave}
            fieldName={element.value}
          />
        )
      }
      return (
        <Box key={element.value} sx={{gridColumn: '1 / -1'}}>
          <Field label={element.name} value={rawValue} />
        </Box>
      )
    }

    if (element.type === 'valueAndUnitType') {
      return <Field key={element.value} label={element.name} value={formatUnitOfMeasure(rawValue)} />
    }

    if (element.type === 'assumptionDosage') {
      if (!therapyHasAssumptionDosage) return null
      return <Field key={element.value} label={element.name} value={formatUnitOfMeasure(rawValue)} />
    }

    if (element.type === 'device') {
      return <Field key={element.value} label={element.name} value={THERAPY_DEVICE_OF_ASSUMPTIONS[rawValue] ?? rawValue} />
    }

    return <Field key={element.value} label={element.name} value={rawValue !== undefined && rawValue !== null ? String(rawValue) : undefined} />
  }

  return (
    <Card>
      <TwoColGrid>
        {generalSchema.map(renderField)}
      </TwoColGrid>

      {shouldRenderCustomDosages && <CustomDosagesBlock therapy={therapy} dataSchema={dataSchema} />}

      {deviceSchema.length > 0 && (
        <Box sx={{mt: 2}}>
          <TwoColGrid>
            {deviceSchema.map(renderField)}
          </TwoColGrid>
        </Box>
      )}

      {notesSchema.length > 0 && (
        <Box sx={{mt: 2}}>
          <TwoColGrid>
            {notesSchema.map(renderField)}
          </TwoColGrid>
        </Box>
      )}
    </Card>
  )
}
